import React, { useState, useEffect } from 'react';
import { Menu, X, Building2, Mail, Home, Users, LayoutDashboard } from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import { getUrlWithCountry } from '../../utils/countryUtils';
import brandConfig from '../../config/brandConfig';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { country, countryConfig } = useCountry();

  useEffect(() => {
    // Mark component as mounted to prevent hydration mismatch
    setIsMounted(true);
    
    // Verificar si el usuario está logueado leyendo la cookie compartida
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    const checkAuth = () => {
      if (typeof window !== 'undefined') {
        const accessToken = getCookie('token') || localStorage.getItem('token');
        setIsLoggedIn(!!accessToken);
      }
    };
    
    checkAuth();
    
    // Escuchar cambios en localStorage
    const handleStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', checkAuth);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', checkAuth);
    };
  }, []);

  // URLs from centralized config
  const dashboardUrl = brandConfig.dashboardUrl;
  const clientHomeUrl = brandConfig.clientHomeUrl;

  return (
    <nav className="shadow-sm sticky top-0 z-50 border-b bg-white/95 dark:bg-gray-900/95 dark:border-gray-700 backdrop-blur-md">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Professional Logo */}
          <a href={getUrlWithCountry('/')} className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity">
            <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
              <img
                src="/images/android-chrome-192x192.png"
                alt={brandConfig.getLogoAlt()}
                width={40}
                height={40}
                className="rounded-lg"
              />
            </div>
            <div>
              <div className="flex items-center gap-1 sm:gap-2">
                <h1 className="text-base sm:text-lg font-bold text-gray-800 dark:text-white">
                  <span style={{ color: brandConfig.primaryColor }}>{brandConfig.namePrefix}</span>{brandConfig.nameSuffix}
                </h1>
                {country && countryConfig && (
                  <span className="text-base sm:text-lg" title={countryConfig.name}>
                    {countryConfig.flag}
                  </span>
                )}
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 hidden sm:block">{brandConfig.slogan}</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <a href={clientHomeUrl} className="text-sm font-medium transition-colors duration-200 relative group text-gray-700 dark:text-gray-200 hover:text-orange-600 dark:hover:text-orange-400">
              Inicio
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-200 group-hover:w-full bg-orange-600"></span>
            </a>
            {/* <a href={getUrlWithCountry('/sobre-este-sitio', country)} className="text-sm font-medium transition-colors duration-200 relative group text-gray-700 dark:text-gray-200 hover:text-orange-600 dark:hover:text-orange-400">
              Sobre Este Sitio
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-200 group-hover:w-full bg-orange-600"></span>
            </a> */}
            <a href={getUrlWithCountry('/como-agregar-proyectos', country)} className="text-sm font-medium transition-colors duration-200 relative group text-gray-700 dark:text-gray-200 hover:text-orange-600 dark:hover:text-orange-400">
            Agregar mis proyectos
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-200 group-hover:w-full bg-orange-600"></span>
            </a>
            <a href={`${brandConfig.clientHomeUrl}#contact`} className="text-sm font-medium transition-colors duration-200 relative group text-gray-700 dark:text-gray-200 hover:text-orange-600 dark:hover:text-orange-400">
              Contáctenos
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-200 group-hover:w-full bg-orange-600"></span>
            </a>
          </div>

          {/* Desktop Actions - Botones al extremo derecho */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href={brandConfig.projectDemoUrl}
              className="flex items-center font-semibold transition-colors duration-200 px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-white border border-gray-300 dark:border-gray-600" 
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Ver Demo
            </a>
            {!isMounted ? null : isLoggedIn ? (
              <a href={`${dashboardUrl}/dashboard`} className="text-white px-4 sm:px-6 py-2 rounded-xl font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl text-sm sm:text-base" style={{ background: brandConfig.getPrimaryGradient() }}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                <span className="hidden xl:inline">Ir a Dashboard</span>
                <span className="xl:hidden">Dashboard</span>
              </a>
            ) : (
              <a href={`${dashboardUrl}/iniciar-sesion`} className="text-white px-4 sm:px-6 py-2 rounded-xl font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl text-sm sm:text-base" style={{ background: brandConfig.getPrimaryGradient() }}>
                <Building2 className="w-4 h-4 mr-2" />
                <span className="hidden xl:inline">Publicar Mis Lotes</span>
                <span className="xl:hidden">Publicar</span>
              </a>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="transition-colors duration-200 p-2 text-gray-800 dark:text-white"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-700">
            <div className="px-2 pt-4 pb-6 space-y-2 bg-gray-50 dark:bg-gray-900">
              <a href={clientHomeUrl} className="block px-4 py-3 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-800 dark:text-white">
                <Home className="w-4 h-4 inline mr-2" />
                Inicio
              </a>
              {/* <a href={getUrlWithCountry('/sobre-este-sitio', country)} className="block px-4 py-3 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-800 dark:text-white">
                <Users className="w-4 h-4 inline mr-2" />
                Sobre Este Sitio
              </a> */}
              <a href={getUrlWithCountry('/como-agregar-proyectos', country)} className="block px-4 py-3 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-800 dark:text-white">
                <Building2 className="w-4 h-4 inline mr-2" />
                Agregar mis proyectos
              </a>
              <a href={`${brandConfig.clientHomeUrl}#contact`} className="block px-4 py-3 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-800 dark:text-white">
                <Mail className="w-4 h-4 inline mr-2" />
                Contáctenos
              </a>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <a 
                  href={brandConfig.projectDemoUrl}
                  className="block w-full text-left px-4 py-3 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-800 dark:text-white border border-gray-300 dark:border-gray-600 mb-2" 
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Ver Demo
                </a>
                {!isMounted ? null : isLoggedIn ? (
                  <a href={`${dashboardUrl}/dashboard`} className="block w-full text-left px-4 py-3 text-white rounded-lg font-semibold mb-2" style={{ background: brandConfig.getPrimaryGradient() }}>
                    <LayoutDashboard className="w-4 h-4 inline mr-2" />
                    Ir a Dashboard
                  </a>
                ) : (
                  <a href={`${dashboardUrl}/iniciar-sesion`} className="block w-full text-left px-4 py-3 text-white rounded-lg font-semibold mb-2" style={{ background: brandConfig.getPrimaryGradient() }}>
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Publicar Mis Lotes
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;

